<template>
  <div class="position-relative">
    <button
        class="btn d-flex align-items-center justify-content-center position-relative p-2 bell-btn"
        type="button"
        aria-label="Notificaciones"
        @click.stop="toggleNotifications"
    >
      <i class="fa-solid fa-bell text-secondary"></i>
      <span
          v-if="unreadCount > 0"
          class="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
      >
        {{ unreadCount }}
      </span>
    </button>

    <div
        v-if="showNotifications"
        class="notif-panel shadow-sm border bg-white position-absolute end-0 mt-2"
        @click.stop
    >
      <div v-if="!visibleNotifications.length" class="px-3 py-2 text-muted small">{{ $t('notification.menu.empty') }}</div>
      <div v-else class="list-group list-group-flush">
        <a
            v-for="item in visibleNotifications"
            :key="item.id"
            class="list-group-item list-group-item-action d-flex align-items-start gap-2 small"
            :class="{ 'fw-semibold': !item.read }"
            href="#"
            @click.prevent="openNotification(item)"
        >
          <i :class="[item.iconClass, 'text-secondary', 'mt-1']"></i>
          <div class="d-flex flex-column">
            <span :class="{ 'fw-semibold': item.status !== 'read' }">{{ item.title }}</span>
            <span class="text-muted">{{ item.metaText }}</span>
          </div>
        </a>
      </div>
      <div
          class="border-top px-3 py-2 text-primary fw-semibold small"
          role="button"
          @click="goToNotifications"
      >
        {{ $t('notification.menu.view_more') }}
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import router from '@/router/router';
import echoService from '@/services/echoService';
import { fetchNotifications, markRead as apiMarkRead } from '@/services/notificationService';

export default {
  name: 'NotificationBell',
  data() {
    return {
      notifications: [],
      showNotifications: false,
      channelName: null,
      totalUnread: null
    };
  },
  computed: {
    ...mapState('user', ['user']),
    unreadCount() {
      if (this.totalUnread !== null && this.totalUnread !== undefined) return this.totalUnread;
      return this.notifications.filter(n => n.status !== 'read').length;
    },
    visibleNotifications() {
      return this.notifications.filter(n => n.status !== 'read').slice(0, 5);
    }
  },
  methods: {
    async loadNotifications() {
      try {
        const res = await fetchNotifications({ page: 1, perPage: 10, status: 'unread' });
        const list = res?.data || res || [];
        const meta = res?.meta || {};
        this.notifications = list.map(this.normalizeItem);
        this.totalUnread = meta.total ?? list.length;
      } catch (e) {
        console.error('Error cargando notificaciones', e);
      }
    },
    toggleNotifications() {
      this.showNotifications = !this.showNotifications;
      if (this.showNotifications && !this.notifications.length) {
        this.loadNotifications();
      }
    },
    goToNotifications() {
      this.showNotifications = false;
      router.push('/notifications');
    },
    async openNotification(item) {
      await this.markRead(item.id);
      this.showNotifications = false;
      router.push({ path: '/notifications', query: { openId: item.id } });
    },
    async markRead(id) {
      if (!id) return;
      try {
        const wasUnread = this.notifications.some(n => n.id === id && n.status !== 'read');
        await apiMarkRead(id);
        this.notifications = this.notifications.map(n =>
            n.id === id ? { ...n, status: 'read', read_at: new Date().toISOString() } : n
        );
        if (wasUnread && this.totalUnread !== null && this.totalUnread !== undefined) {
          this.totalUnread = Math.max(0, this.totalUnread - 1);
        }
      } catch (e) {
        console.error('Error marcando notificación', e);
      }
    },
    handleExternalRead(event) {
      const id = Number(event?.detail?.id ?? event?.detail);
      if (!id) return;
      const wasUnread = this.notifications.some(n => n.id === id && n.status !== 'read');
      this.notifications = this.notifications.map(n =>
          n.id === id ? { ...n, status: 'read', read_at: new Date().toISOString() } : n
      );
      if (wasUnread && this.totalUnread !== null && this.totalUnread !== undefined) {
        this.totalUnread = Math.max(0, this.totalUnread - 1);
      }
    },
    handleOutsideClick(event) {
      const dropdownEl = this.$el?.querySelector('.notif-panel');
      const bellBtn = this.$el?.querySelector('.bell-btn');
      if (!dropdownEl || !bellBtn) return;
      if (!dropdownEl.contains(event.target) && !bellBtn.contains(event.target)) {
        this.showNotifications = false;
      }
    },
    subscribeToChannel() {
      if (!this.user?.id) return;
      // Echo agrega el prefijo "private-" automáticamente, por eso usamos el nombre sin el prefijo
      const channelName = `notifications.${this.user.id}`;
      // Si ya estamos suscritos al mismo canal, no duplicar
      if (this.channelName === channelName) return;
      // Limpia suscripción previa si cambia el usuario
      if (this.channelName) {
        echoService.leave(this.channelName);
      }
      this.channelName = channelName;

      const channel = echoService.private(channelName);

      const onCreated = (payload) => {
        const notification = this.extractNotification(payload);
        if (!notification) return;
        const normalized = this.normalizeItem(notification);
        const existingIdx = this.notifications.findIndex(n => n.id === normalized.id);
        if (existingIdx !== -1) {
          this.notifications.splice(existingIdx, 1);
        }
        this.notifications = [normalized, ...this.notifications];
        if (this.totalUnread !== null && this.totalUnread !== undefined) {
          this.totalUnread += 1;
        } else {
          this.totalUnread = this.notifications.filter(n => n.status !== 'read').length;
        }
        window.dispatchEvent(new CustomEvent('notification:created', { detail: normalized }));
      };

      channel.listen('NotificationCreated', onCreated);
      channel.listen('.NotificationCreated', onCreated);

      channel.listen('NotificationRead', (payload) => {
        if (!payload?.notification_id) return;
        let decremented = false;
        this.notifications = this.notifications.map(n => {
          if (n.id !== payload.notification_id) return n;
          if (n.status !== 'read') {
            decremented = true;
          }
          return { ...n, status: 'read', read_at: payload.read_at || n.read_at }
        });
        if (decremented && this.totalUnread !== null && this.totalUnread !== undefined) {
          this.totalUnread = Math.max(0, this.totalUnread - 1);
        }
      });

      channel.listen('NotificationUpdated', (payload) => {
        const notification = this.extractNotification(payload);
        if (!notification) return;
        const normalized = this.normalizeItem(notification);
        const existingIdx = this.notifications.findIndex(n => n.id === normalized.id);
        if (existingIdx !== -1) {
          this.notifications.splice(existingIdx, 1, normalized);
        } else {
          this.notifications = [normalized, ...this.notifications];
        }
      });
    },
    normalizeItem(n) {
      if (!n) return n;
      const payload = n.payload || n.meta || {};
      const metaText = this.composeMetaText(payload);
      const key = n.key || n.type || '';
      const fallbackTitle = n.title || key || 'Notificación';
      const fallbackMessage = n.message || n.detail || key || '';
      const actions = this.buildActions(n);
      return {
        ...n,
        iconClass: this.normalizeIcon(n.icon),
        metaText,
        title: this.getTranslation(key, payload, 'title', fallbackTitle),
        message: this.getTranslation(key, payload, 'message', fallbackMessage),
        payload,
        actions
      };
    },
    buildActions(n) {
      const action = n.action;
      if (!action) return [];

      const contextPayload = n.payload || n.meta || {};
      const baseAction = { ...action, contextPayload };

      const rawOptions = Array.isArray(action.options)
          ? action.options
          : (action.options && typeof action.options === 'object')
              ? Object.values(action.options)
              : null;

      if (rawOptions && rawOptions.length) {
        return rawOptions
            .map(opt => this.normalizeActionOption(baseAction, opt))
            .filter(Boolean);
      }

      return [this.normalizeActionOption(baseAction, action)].filter(Boolean);
    },
    normalizeActionOption(base, option = {}) {
      const merged = { ...base, ...option };
      const contextPayload = merged.contextPayload || {};
      const kind = this.resolveActionKind(merged);
      const status = merged.status ?? merged.status_id ?? merged.new_status ?? contextPayload.status_id ?? (kind === 'accept' ? 3 : kind === 'decline' ? 5 : undefined);
      const type = merged.type || (status !== undefined ? 'status' : 'navigate');

      return {
        label: merged.label || this.actionLabel(kind),
        variant: merged.variant || this.actionVariant(kind),
        iconClass: merged.iconClass || merged.icon || this.actionIcon(kind),
        type,
        route: merged.route,
        params: merged.params || {},
        path: merged.path || null,
        entity: merged.entity || merged.resource || merged.role || merged.target || merged.subject || contextPayload.entity || contextPayload.role,
        status,
        id: merged.id ?? merged.resource_id ?? merged.competition_user_id ?? merged.target_id ?? merged.subject_id ?? contextPayload.competition_user_id ?? contextPayload.id,
        source: merged.source || null
      };
    },
    resolveActionKind(action = {}) {
      const rawType = (action.type || action.name || '').toString().toLowerCase();
      const status = action.status ?? action.status_id ?? action.new_status;
      const payloadStatus = action.contextPayload?.status_id;
      const combinedStatus = status ?? payloadStatus;

      if ([3, 4].includes(Number(combinedStatus))) return 'accept';
      if (Number(combinedStatus) === 5) return 'decline';
      if (['accept', 'approve', 'confirm', 'ok', 'yes'].includes(rawType)) return 'accept';
      if (['decline', 'reject', 'deny', 'cancel', 'no'].includes(rawType)) return 'decline';
      return 'view';
    },
    actionLabel(kind) {
      const map = {
        accept: this.$t ? this.$t('notification.action.accept') : 'Accept',
        decline: this.$t ? this.$t('notification.action.decline') : 'Decline',
        view: this.$t ? this.$t('notification.action.view') : 'View'
      };
      return map[kind] || map.view;
    },
    actionVariant(kind) {
      if (kind === 'accept') return 'success';
      if (kind === 'decline') return 'danger';
      return 'primary';
    },
    actionIcon(kind) {
      if (kind === 'accept') return 'fa-solid fa-thumbs-up';
      if (kind === 'decline') return 'fa-solid fa-user-slash';
      return 'fa-solid fa-arrow-right';
    },
    extractNotification(payload) {
      if (!payload) return null;
      if (typeof payload === 'string') {
        try {
          const parsed = JSON.parse(payload);
          return this.extractNotification(parsed);
        } catch (e) {
          return null;
        }
      }
      // If it already looks like a notification with id/key, return as-is
      if (payload.id && (payload.key || payload.type)) return payload;

      if (payload?.notification) return payload.notification;
      const dataField = payload?.data || payload?.payload;
      if (dataField) {
        try {
          const parsed = typeof dataField === 'string' ? JSON.parse(dataField) : dataField;
          if (parsed?.notification) return parsed.notification;
          if (parsed?.id && (parsed?.key || parsed?.type)) return parsed;
          return null;
        } catch (e) {
          console.error('Error parsing notification payload', e);
          return null;
        }
      }
      if (payload?.event && payload?.channel && payload?.data === undefined) {
        return null;
      }
      return null;
    },
    composeMetaText(meta = {}) {
      const parts = [];
      if (meta.competition) parts.push(meta.competition);
      if (meta.round) parts.push(meta.round);
      if (meta.group) parts.push(meta.group);
      if (meta.role) parts.push(meta.role);
      if (meta.status) parts.push(meta.status);
      if (meta.location) parts.push(meta.location);
      if (meta.document) parts.push(meta.document);
      if (meta.starts_in) parts.push(`Empieza en ${meta.starts_in}`);
      if (meta.window) parts.push(`Ventana ${meta.window}`);
      return parts.join(' · ');
    },
    getTranslation(key, payload = {}, variant, fallback = '') {
      if (!key) return fallback;
      const paths = this.buildPaths(key, variant);
      for (const p of paths) {
        if (this.canTranslate(p)) {
          return this.$t ? this.$t(p, payload) : fallback || key;
        }
      }
      return fallback || key;
    },
    buildPaths(key, variant) {
      const cleanKey = key.replace(/^\.+/, '');
      const base = cleanKey.startsWith('notification.') ? cleanKey : `notification.${cleanKey}`;
      const alt = base.replace(/^notification\./, 'notifications.');
      const deep = alt.startsWith('notifications.notification.') ? alt : `notifications.notification.${cleanKey.replace(/^notification\./, '')}`;
      return [`${base}.${variant}`, `${alt}.${variant}`, `${deep}.${variant}`];
    },
    canTranslate(path) {
      if (this.$te && this.$te(path)) return true;
      const i18n = this.$i18n || this.$root?.$i18n || this.$i18n?.global;
      if (!i18n) return false;
      const locale = i18n.locale || i18n.global?.locale;
      const messages = i18n.global?.messages?.[locale] || i18n.messages?.[locale];
      if (!messages) return false;
      const segments = path.split('.');
      let node = messages;
      for (const s of segments) {
        if (node && Object.prototype.hasOwnProperty.call(node, s)) {
          node = node[s];
        } else {
          return false;
        }
      }
      return typeof node === 'string';
    },
    normalizeIcon(icon) {
      if (!icon) return 'fa-solid fa-bell';
      if (icon.includes('fa-')) return icon;
      return `fa-solid fa-${icon}`;
    }
  },
  mounted() {
    this.loadNotifications();
    this.subscribeToChannel();
    document.addEventListener('click', this.handleOutsideClick);
    window.addEventListener('notification:read', this.handleExternalRead);
  },
  watch: {
    user(val) {
      // Si el usuario se carga después, suscríbete y carga notificaciones
      if (val?.id) {
        this.subscribeToChannel();
        if (!this.notifications.length) {
          this.loadNotifications();
        }
      }
    }
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick);
    window.removeEventListener('notification:read', this.handleExternalRead);
    if (this.channelName) {
      echoService.leave(this.channelName);
    }
  }
};
</script>

<style scoped>
.bell-btn {
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 4px;
  background-color: transparent;
  min-width: 40px;
  min-height: 40px;
}
.notif-panel {
  min-width: 260px;
  max-width: 320px;
  border-radius: 8px;
  overflow: hidden;
  z-index: 2000;
}
</style>
